/**
 * Namespacing for variables inserted inside a REPEATABLE group (the context
 * menu's "Make it repeatable") or a legacy list-bound container.
 *
 * A list-bound container (slide / combo / set carrying
 * `settings.listDynamicVariable`) is cloned once per data row at render time.
 * A variable inserted inside it therefore has to say WHICH list row field it
 * refers to, so its token is namespaced with the list variable's name:
 * `{{first_name}}` → `{{array.first_name}}` inside a list named `array`.
 *
 * The runtime substitutes those namespaced tokens per cloned row (see
 * parse-list-dynamic-variable). Kept pure so both insert paths — click-to-insert
 * and drag-and-drop — share exactly one implementation.
 */

/** Minimal shape needed to walk the flat entity map upwards. */
type NamespaceEntity = {
  parentId?: string | null;
  settings?: Record<string, unknown>;
};

/** `{{name}}` / `[name]` → `name`; already-bare names pass through. */
export const bareTokenName = (value: string): string =>
  value
    .replace(/^\{\{+/, "")
    .replace(/\}\}+$/, "")
    .replace(/^\[/, "")
    .replace(/\]$/, "")
    .trim();

/**
 * Name of the list variable bound to `entityId` or any of its ancestors, or
 * null when the entity is not inside a list-bound container. The nearest
 * binding wins, so a list nested inside another list namespaces with its own.
 */
export const findListBindingName = (
  entities: Record<string, NamespaceEntity | undefined> | undefined | null,
  entityId: string | null | undefined,
): string | null => {
  if (!entities || !entityId) return null;

  const seen = new Set<string>();
  let currentId: string | null | undefined = entityId;

  while (currentId && !seen.has(currentId)) {
    seen.add(currentId);
    const entity: NamespaceEntity | undefined = entities[currentId];
    if (!entity) return null;
    const binding = entity.settings?.listDynamicVariable;
    if (typeof binding === "string" && bareTokenName(binding)) {
      return bareTokenName(binding);
    }
    currentId = entity.parentId;
  }
  return null;
};

/**
 * Prefixes a variable token with its list namespace. Returns the token
 * unchanged when there is no list, when it is already namespaced for that list,
 * or when it is not a `{{…}}` token. Preserves triple-brace (raw HTML) tokens.
 */
export const namespaceListToken = (
  token: string,
  listName: string | null,
): string => {
  if (!listName || !token) return token;

  const match = /^(\{\{\{?)([^{}]+)(\}?\}\})$/.exec(token.trim());
  if (!match) return token;

  const [, open, inner, close] = match;
  const name = inner.trim();
  if (!name) return token;
  // Idempotent: re-inserting an already-namespaced token must not double it up.
  if (name === listName || name.startsWith(`${listName}.`)) return token;

  return `${open}${listName}.${name}${close}`;
};

/** Just enough of a `Repeatable` to locate its scope. */
type RepeatableScope = { key: string; entityId: string };

/**
 * Key of the repeatable group whose marked entity is `entityId` or one of its
 * ancestors — the group a variable inserted here belongs to. The NEAREST group
 * wins, so a repeatable nested inside another namespaces with its own key.
 *
 * A repeatable's fields are namespaced `{{<group.key>.<field.key>}}` (see
 * repeatable/types.ts), which is exactly the token shape produced here.
 */
export const findRepeatableKeyForEntity = (
  entities: Record<string, NamespaceEntity | undefined> | undefined | null,
  entityId: string | null | undefined,
  repeatables: RepeatableScope[] | undefined | null,
): string | null => {
  if (!entities || !entityId || !repeatables?.length) return null;

  const byEntityId = new Map<string, string>();
  for (const group of repeatables) {
    if (group?.entityId && group.key && !byEntityId.has(group.entityId)) {
      byEntityId.set(group.entityId, group.key);
    }
  }
  if (byEntityId.size === 0) return null;

  const seen = new Set<string>();
  let currentId: string | null | undefined = entityId;
  while (currentId && !seen.has(currentId)) {
    seen.add(currentId);
    const key = byEntityId.get(currentId);
    if (key) return key;
    const entity: NamespaceEntity | undefined = entities[currentId];
    if (!entity) return null;
    currentId = entity.parentId;
  }
  return null;
};

/**
 * The namespace an inserted variable should use at `entityId`: the enclosing
 * repeatable group's key, else a legacy list binding, else none.
 */
export const findInsertNamespace = (
  entities: Record<string, NamespaceEntity | undefined> | undefined | null,
  entityId: string | null | undefined,
  repeatables?: RepeatableScope[] | null,
): string | null =>
  findRepeatableKeyForEntity(entities, entityId, repeatables) ??
  findListBindingName(entities, entityId);
